import React, { useState, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import ProfilePictureHover from '../../../components/ProfilePictureHover';
import {
  IcoZap, IcoRefresh, IcoUsers, fmt,
  EmployeeRole, UngeneratedEmployee,
} from '../PayrollManagement';
import PayrollOverridesModal, { OverrideRow, OverrideMode } from './PayrollOverridesModal';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface Props {
  onGenerated: () => void;
  toast: React.RefObject<Toast | null>;
  /**
   * 'card'  — the dashed-border card used on the Salaries tab (default).
   * 'bare'  — no outer card/header, so this can be dropped into another
   *           container (e.g. GeneratePayrollModal's pr-modal-body) without
   *           doubling up on chrome.
   */
  variant?: 'card' | 'bare';
  /**
   * When set (e.g. opened from the "not generated" strip for a specific
   * employee), pre-fills and locks the Employee ID / Role fields so the
   * generate call is scoped to just that person.
   */
  prefillEmployee?: UngeneratedEmployee;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main GeneratePayrollPanel
// ─────────────────────────────────────────────────────────────────────────────
const GeneratePayrollPanel: React.FC<Props> = ({ onGenerated, toast, variant = 'card', prefillEmployee }) => {
  const [monthYear, setMonthYear] = useState<string>(() => new Date().toISOString().substring(0, 7));
  const [session,   setSession]   = useState<string>('');
  const [empId,     setEmpId]     = useState('');
  const [empRole,   setEmpRole]   = useState<EmployeeRole | ''>('');

  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState<any>(null);

  // Per-employee overrides — used only when NOT scoped to a single employee.
  const [overrides, setOverrides] = useState<OverrideRow[]>([]);
  const [overridesModalOpen, setOverridesModalOpen] = useState(false);

  // Single-employee override fields — used only when scoped (isLocked).
  // These replace the need to open the full overrides modal for one person.
  const [singleMode,            setSingleMode]            = useState<OverrideMode>('auto');
  const [singleDeductDays,      setSingleDeductDays]       = useState('');
  const [singleCustomDeduction, setSingleCustomDeduction]  = useState('');
  const [singleReason,          setSingleReason]           = useState('');

  const isLocked = !!prefillEmployee;

  // Pre-fill employee id/role whenever a prefillEmployee is passed in
  // (e.g. each time a different "not generated" employee is chosen).
  useEffect(() => {
    if (prefillEmployee) {
      setEmpId(String(prefillEmployee.employee_id));
      setEmpRole(prefillEmployee.employee_role as EmployeeRole);
      // Reset single-employee override fields for the newly-selected person.
      setSingleMode('auto');
      setSingleDeductDays('');
      setSingleCustomDeduction('');
      setSingleReason('');
    }
  }, [prefillEmployee]);

  const handleGenerate = async () => {
    if (!monthYear || !session) {
      toast.current?.show({ severity: 'warn', summary: 'Missing fields', detail: 'Month and session are required.', life: 3000 });
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const body: any = { month_year: monthYear, session };
      if (empId && empRole) { body.employee_id = empId; body.employee_role = empRole; }

      // When scoped to a single employee, build the override list from the
      // inline mode/days/deduction/reason fields instead of the multi-employee
      // overrides modal. Otherwise, use whatever the overrides modal produced.
      const effectiveOverrides: OverrideRow[] =
        isLocked && prefillEmployee
          ? (singleMode !== 'auto' || (singleCustomDeduction && +singleCustomDeduction > 0)
              ? [{
                  employee_id: prefillEmployee.employee_id,
                  employee_role: prefillEmployee.employee_role as EmployeeRole,
                  employee_name: prefillEmployee.employee_name ?? '',
                  job_profile: prefillEmployee.role ?? '',
                  mode: singleMode,
                  deduct_days: singleDeductDays,
                  custom_deduction: singleCustomDeduction,
                  custom_deduction_reason: singleReason,
                }]
              : [])
          : overrides;

      if (effectiveOverrides.length > 0) {
        body.overrides = effectiveOverrides.map(r => ({
          employee_id: r.employee_id,
          employee_role: r.employee_role,
          mode: r.mode,
          ...(r.mode === 'custom_days' ? { deduct_days: +r.deduct_days } : {}),
          ...(r.custom_deduction && +r.custom_deduction > 0 ? {
            custom_deduction: +r.custom_deduction,
            ...(r.custom_deduction_reason.trim() ? { custom_deduction_reason: r.custom_deduction_reason.trim() } : {}),
          } : {}),
        }));
      }

      const res = await apiClient.post('/payroll/generate', body);
      setResult(res.data);
      if (res.data.status === 'success' || res.data.status === 'partial_success') {
        onGenerated();
        toast.current?.show({ severity: res.data.status === 'success' ? 'success' : 'warn', summary: 'Done', detail: res.data.message, life: 4000 });
      } else {
        toast.current?.show({ severity: 'error', summary: 'Failed', detail: res.data.message, life: 4000 });
      }
    } catch (e: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || 'Generation failed', life: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const successCount = result?.results?.filter((r: any) => r.status === 'success').length ?? 0;
  const skipCount    = result?.results?.filter((r: any) => r.status === 'skipped').length ?? 0;
  const errCount     = result?.results?.filter((r: any) => r.status === 'error').length ?? 0;

  const totalAdvanceDeducted = result?.results
    ?.filter((r: any) => r.status === 'success' && r.advance_deducted > 0)
    ?.reduce((sum: number, r: any) => sum + Number(r.advance_deducted), 0) ?? 0;

  const totalCustomDeducted = result?.results
    ?.filter((r: any) => r.status === 'success' && r.custom_deduction > 0)
    ?.reduce((sum: number, r: any) => sum + Number(r.custom_deduction), 0) ?? 0;

  const overriddenCount = result?.results
    ?.filter((r: any) => r.status === 'success' && r.mode && r.mode !== 'auto').length ?? 0;

  const content = (
    <>
      {variant === 'card' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#0f766e,#0d9488)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 3px 8px rgba(15,118,110,.3)' }}>
            <IcoZap size={16} color="white" />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Generate payroll</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Runs for all employees unless a specific one is chosen. Pending advances are auto-deducted.
            </div>
          </div>
        </div>
      )}

      {isLocked && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 12px', borderRadius: 10,
          background: 'rgba(15,118,110,.08)', border: '1px solid rgba(15,118,110,.2)',
        }}>
          <ProfilePictureHover
            src={prefillEmployee?.profile_picture ?? ''}
            name={prefillEmployee?.employee_name ?? `#${prefillEmployee?.employee_id}`}
            size={30}
            borderRadius={8}
          />
          <div style={{ fontSize: 12, color: '#0f766e', fontWeight: 600 }}>
            Scoped to {prefillEmployee?.employee_name ?? `#${prefillEmployee?.employee_id}`} ({prefillEmployee?.employee_role})
          </div>
        </div>
      )}

      {/* Core fields */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="pr-field">
          <label className="pr-label">Month *</label>
          <input type="month" className="pr-input" value={monthYear} onChange={e => setMonthYear(e.target.value)} />
        </div>
        <div className="pr-field">
          <label className="pr-label">Session *</label>
          <input type="text" className="pr-input" placeholder="e.g. 2026-27" value={session} onChange={e => setSession(e.target.value)} />
        </div>
        <div className="pr-field">
          <label className="pr-label">Employee ID (optional)</label>
          <input
            type="number" className="pr-input" placeholder="Leave blank for all"
            value={empId} onChange={e => setEmpId(e.target.value)}
            disabled={isLocked}
            style={isLocked ? { background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' } : undefined}
          />
        </div>
        <div className="pr-field">
          <label className="pr-label">Role (optional)</label>
          <select
            className="pr-select" value={empRole} onChange={e => setEmpRole(e.target.value as any)}
            disabled={isLocked}
            style={isLocked ? { background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' } : undefined}
          >
            <option value="">— Any —</option>
            <option value="teacher">Teacher</option>
            <option value="staff">Staff</option>
            <option value="principal">Principal</option>
          </select>
        </div>
      </div>

      {/* Single-employee override fields — shown only when scoped to one person */}
      {isLocked ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: singleMode === 'custom_days' ? '1fr 1fr' : '1fr', gap: 12 }}>
            <div className="pr-field">
              <label className="pr-label">Mode</label>
              <select className="pr-select" value={singleMode} onChange={e => setSingleMode(e.target.value as OverrideMode)}>
                <option value="auto">Auto</option>
                <option value="full">Full salary</option>
                <option value="custom_days">Custom days</option>
              </select>
            </div>
            {singleMode === 'custom_days' && (
              <div className="pr-field">
                <label className="pr-label">Days to deduct</label>
                <input
                  type="number" min="0" className="pr-input"
                  value={singleDeductDays} onChange={e => setSingleDeductDays(e.target.value)}
                />
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="pr-field">
              <label className="pr-label">Custom deduction</label>
              <input
                type="number" min="0" className="pr-input" placeholder="₹0"
                value={singleCustomDeduction} onChange={e => setSingleCustomDeduction(e.target.value)}
              />
            </div>
            <div className="pr-field">
              <label className="pr-label">Reason</label>
              <input
                type="text" className="pr-input" placeholder="Optional"
                value={singleReason} onChange={e => setSingleReason(e.target.value)}
              />
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="pr-btn pr-btn--ghost"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => setOverridesModalOpen(true)}
        >
          <IcoUsers size={13} color="#0f766e" />
          Per-employee overrides
          {overrides.length > 0 && (
            <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'rgba(15,118,110,.15)', color: '#0f766e' }}>
              {overrides.length}
            </span>
          )}
        </button>
      )}

      {/* Generate button + result badges */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button className="pr-btn pr-btn--primary" onClick={handleGenerate} disabled={loading}>
          {loading
            ? <IcoRefresh size={14} color="white" style={{ animation: 'pr-spin 1s linear infinite' }} />
            : <IcoZap size={14} color="white" />}
          {loading ? 'Generating…' : 'Generate'}
        </button>

        {result && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', animation: 'pr-fadeIn .3s ease' }}>
            {successCount > 0 && (
              <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(34,197,94,.1)', color: '#15803d' }}>
                ✓ {successCount} generated
              </span>
            )}
            {skipCount > 0 && (
              <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(99,102,241,.1)', color: '#4338ca' }}>
                ↷ {skipCount} skipped
              </span>
            )}
            {errCount > 0 && (
              <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(239,68,68,.1)', color: '#dc2626' }}>
                ✕ {errCount} failed
              </span>
            )}
            {totalAdvanceDeducted > 0 && (
              <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(245,158,11,.1)', color: '#b45309' }}>
                ↓ {fmt(totalAdvanceDeducted)} advances deducted
              </span>
            )}
            {totalCustomDeducted > 0 && (
              <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(180,83,9,.1)', color: '#b45309' }}>
                ↓ {fmt(totalCustomDeducted)} custom deduction
              </span>
            )}
            {overriddenCount > 0 && (
              <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(180,83,9,.1)', color: '#b45309' }}>
                ⚠ {overriddenCount} employee(s) overridden
              </span>
            )}
          </div>
        )}
      </div>

      {!isLocked && (
        <PayrollOverridesModal
          open={overridesModalOpen}
          onClose={() => setOverridesModalOpen(false)}
          onApply={setOverrides}
          initialOverrides={overrides}
          toast={toast}
        />
      )}
    </>
  );

  if (variant === 'bare') {
    return <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{content}</div>;
  }

  return <div className="pr-generate-card">{content}</div>;
};

export default GeneratePayrollPanel;